export const fetchWithRetry = async (request, retries = 2, delay = 1000) => {
  /**
   * Determines whether a thrown fetch error should skip retry attempts.
   * Covers proxy-auth/network failures that often surface as generic TypeError.
   * @param {unknown} err
   * @returns {boolean}
   */
  const isNonRetryableFetchError = (err) => {
    const errorMessage = String(err?.message || '').toLowerCase();
    const isProxyAuthError =
      errorMessage.includes('unexpected_proxy_auth')
      || errorMessage.includes('proxy auth')
      || errorMessage.includes('proxy-auth')
      || errorMessage.includes('proxy');

    const isFailedToFetchTypeError =
      err instanceof TypeError
      && (
        errorMessage.includes('failed to fetch')
        || errorMessage.includes('networkerror')
        || errorMessage.includes('load failed')
      );

    return isProxyAuthError || isFailedToFetchTypeError;
  };

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Clone the request each time so body isn't consumed
      const clonedRequest = request.clone();

      const response = await fetch(clonedRequest);

      if (response.ok) {
        return response;
      }

      // Retry only for specific errors
      if (response.status  === 408 || response.status === 500) {
        if (attempt < retries) {
          await new Promise(res => setTimeout(res, delay));
          continue; // Retry
        }
      }

      // Other errors – don't retry, just return
      return response;
    } catch (err) {
      // Do not retry proxy/network failures with no HTTP response.
      if (isNonRetryableFetchError(err)) {
        const normalizedError = new Error(String(err?.message || 'Network request failed'));
        normalizedError.name = 'FetchNetworkError';
        normalizedError.code = 'NETWORK_NO_RESPONSE';
        normalizedError.cause = err;
        normalizedError.requestUrl = request?.url || '';
        throw normalizedError;
      }

      if (attempt < retries) {
        await new Promise(res => setTimeout(res, delay));
        continue; // Retry
      }
      throw err; // All retries exhausted
    }
  }
};
